import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { City } from '@/data/cities';
import { CityLanguage } from '@/context/languages/languageData';
import { generateCityTranslations } from '@/utils/cityTranslationGenerator';
import { detectCityLanguage } from '@/utils/cityLanguageDetector';
import { supabase } from '@/integrations/supabase/client';

interface CityTranslations {
  [key: string]: string;
}

interface UseCityTranslationsResult {
  translations: CityTranslations;
  isLoading: boolean;
  error: string | null;
  hasTranslation: boolean;
}

export const useCityTranslations = (
  city: City | null,
  language: CityLanguage
): UseCityTranslationsResult => {
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Generate safe default translations
  const defaultTranslations: CityTranslations = React.useMemo(() => {
    if (!city) return {};
    
    return {
      title: city.name || 'City',
      description: `Mental health services in ${city.name || 'this city'}`,
      metaDescription: `Find qualified therapists in ${city.name || 'this city'}`,
      h1: `Therapists in ${city.name || 'City'}`,
      heroTitle: `Find Your Therapist in ${city.name || 'City'}`,
      heroDescription: `Connect with certified mental health professionals in ${city.name || 'this city'}`,
    };
  }, [city]);

  // Fetch from Supabase with React Query
  const { data: dbTranslations, isLoading: isLoadingDb } = useQuery({
    queryKey: ['city-translations', city?.slug, language],
    queryFn: async () => {
      if (!city) return null;

      try {
        const { data, error } = await supabase
          .from('city_translations')
          .select('data')
          .eq('city_slug', city.slug)
          .eq('language', language)
          .is('specialty_slug', null) // Only get main city page translations
          .single();

        if (error && error.code !== 'PGRST116') { // PGRST116 = not found
          console.warn('Translation fetch failed:', error);
          return null; // Return null instead of throwing
        }

        return data?.data as CityTranslations || null;
      } catch (e) {
        console.error('Unexpected translation error:', e);
        return null; // Always return null on error, never throw
      }
    },
    enabled: !!city,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  // Fallback logic with safe defaults
  const translations = (() => {
    if (!city) return defaultTranslations;

    // 1. Use database translations if available
    if (dbTranslations) {
      return { ...defaultTranslations, ...dbTranslations };
    }

    // 2. Check localStorage cache
    try {
      const cacheKey = `city_${city.slug}_${language}`;
      const cachedData = localStorage.getItem(cacheKey);
      if (cachedData) {
        const parsed = JSON.parse(cachedData) as CityTranslations;
        return { ...defaultTranslations, ...parsed };
      }
    } catch (e) {
      console.warn('Error parsing cached translations:', e);
      // Continue to fallback generation
    }

    // 3. Generate fallback from templates
    try {
      const generated = generateCityTranslations(city);
      const langTranslations: CityTranslations = {};
      
      Object.entries(generated).forEach(([key, value]) => {
        if (typeof value === 'object' && value !== null) {
          langTranslations[key] = (value as Record<CityLanguage, string>)[language] || 
                                 (value as Record<CityLanguage, string>)['en'] || 
                                 '';
        }
      });

      return { ...defaultTranslations, ...langTranslations };
    } catch (e) {
      console.warn('Error generating translations:', e);
      return defaultTranslations;
    }
  })();

  // Subscribe to Realtime translation updates
  useEffect(() => {
    if (!city) return;

    const channel = supabase.channel('translation-updates')
      .on('broadcast', { event: 'translation_completed' }, (payload: any) => {
        const { city_slug, language: translationLang, translations } = payload.payload;
        
        // Check if this update is for our current city and language
        if (city_slug === city.slug && translationLang === language) {
          console.log('🔄 Realtime translation update received for', city_slug, language);
          
          // Update React Query cache immediately
          queryClient.setQueryData(
            ['city-translations', city.slug, language],
            { data: translations[language] }
          );
          
          // Also cache to localStorage for persistence
          const cacheKey = `city_${city.slug}_${language}`;
          localStorage.setItem(cacheKey, JSON.stringify(translations[language]));
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [city, language, queryClient]);

  // Validate language support
  useEffect(() => {
    if (!city) return;

    const localLanguage = detectCityLanguage(city);
    if (language !== 'en' && language !== localLanguage) {
      setError(`Language ${language} not available for this city`);
    } else {
      setError(null);
    }
  }, [city, language]);

  return {
    translations,
    isLoading: isLoadingDb,
    error,
    hasTranslation: Object.keys(translations).length > 0
  };
};
